import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Grid,
  Stack,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddTaskIcon from '@mui/icons-material/AddTask';
import PersonIcon from '@mui/icons-material/Person';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import { addTaskRequest, fetchStaffsRequest, fetchClinicProfileByIdRequest } from '../../store/appActions';
import { getUserFromToken } from 'src/DecodeToken';
import storage from 'src/utils/storageUtils';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: '#16A34A', bg: '#DCFCE7' },
  { value: 'medium', label: 'Medium', color: '#D97706', bg: '#FEF3C7' },
  { value: 'high', label: 'High', color: '#DC2626', bg: '#FEE2E2' },
];

function AddTask({ onClose, onSave, onPatientAdded }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [allStaffOptions, setAllStaffOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const isDark = theme.palette.mode === 'dark';
  const selectedClinicId = localStorage.getItem('selectedClinicId') || storage.get('SELECTED_CLINIC_ID') || storage.get('CLINIC_ID');

  useEffect(() => {
    const fetchData = async () => {
      let combinedOptions = [];
      const effectiveClinicId = selectedClinicId || localStorage.getItem('selectedClinicId');

      // 1. Fetch general staff list
      try {
        const staffResponse = await new Promise((resolve, reject) =>
          dispatch(fetchStaffsRequest(effectiveClinicId, resolve, reject)),
        );

        const staffList = Array.isArray(staffResponse)
          ? staffResponse
          : staffResponse?.data && Array.isArray(staffResponse.data)
          ? staffResponse.data
          : [];

        if (staffList.length > 0) {
          const mappedStaff = staffList.map((staff) => {
            const staffEmail = staff.email || staff.username || '';
            const staffName = staff.name || staff.fullName || '';
            return {
              label: staffName && staffEmail ? `${staffName} (${staffEmail})` : (staffName || staffEmail),
              value: staffEmail || staffName,
              name: staffName || staffEmail,
            };
          }).filter(s => s.value);
          combinedOptions = [...combinedOptions, ...mappedStaff];
        }
      } catch (error) {
        console.error('Failed to fetch general staff list', error);
      }

      // 2. Fetch clinic profile to get 'automaticTaskAssignTo' emails
      if (effectiveClinicId) {
        try {
          const clinicResponse = await new Promise((resolve, reject) =>
            dispatch(fetchClinicProfileByIdRequest(effectiveClinicId, resolve, reject))
          );

          if (clinicResponse?.clinicSetting?.automaticTaskAssignTo) {
            const assignTo = clinicResponse.clinicSetting.automaticTaskAssignTo;
            if (Array.isArray(assignTo)) {
              const mappedClinicStaff = assignTo.map((email) => ({
                label: email,
                value: email,
                name: email,
              })).filter(s => s.value);
              combinedOptions = [...combinedOptions, ...mappedClinicStaff];
            }
          }
        } catch (error) {
          console.error('Failed to fetch clinic profile for staff list', error);
        }
      }

      // 3. Remove duplicates based on email/value
      const uniqueOptions = combinedOptions.filter(item => item && item.value).reduce((acc, current) => {
        const x = acc.find((item) => item.value.toLowerCase() === current.value.toLowerCase());
        if (!x) {
          return acc.concat([current]);
        } else {
          if (current.label !== current.value && x.label === x.value) {
            return acc.map((item) => (item.value.toLowerCase() === current.value.toLowerCase() ? current : item));
          }
          return acc;
        }
      }, []);

      setAllStaffOptions(uniqueOptions);
    };

    fetchData();
  }, [dispatch, selectedClinicId]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Task Title is required'),
    description: Yup.string().required('Description is required'),
    assigned: Yup.array().min(1, 'Please assign to at least one staff member'),
    priority: Yup.string().required('Priority is required'),
    dueDate: Yup.string().required('Due Date is required'),
  });

  const handleSubmit = async (values, { resetForm }) => {
    const userId = getUserFromToken();
    if (!userId) {
      enqueueSnackbar('Failed to identify user. Please login again.', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    const taskData = {
      ...values,
      user: userId,
    };

    try {
      await new Promise((resolve, reject) => {
        dispatch(addTaskRequest(taskData, resolve, reject));
      });
      enqueueSnackbar('Task added successfully', { variant: 'success' });
      if (typeof onPatientAdded === 'function') onPatientAdded();
      if (typeof onSave === 'function') onSave();
      if (typeof onClose === 'function') onClose();
      resetForm();
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to add task', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          py: 2.2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? alpha(theme.palette.background.default, 0.6) : '#F8FAFC',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddTaskIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', color: theme.palette.text.primary, lineHeight: 1.2 }}>
              Add Staff Task
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
              Create and assign front desk action items & follow-ups
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            borderRadius: '10px',
            p: 0.8,
            '&:hover': {
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              color: theme.palette.text.primary,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Form Content */}
      <Formik
        initialValues={{
          name: '',
          description: '',
          assigned: [],
          priority: 'high',
          taskStatus: 'pending',
          dueDate: dayjs().format('YYYY-MM-DD'),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form>
            <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack spacing={2.5}>
                {/* Task Title */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                    Task Title <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
                  </Typography>
                  <TextField
                    name="name"
                    placeholder="e.g. Call patient for follow-up report"
                    value={values.name}
                    onChange={handleChange}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                      sx: {
                        borderRadius: '10px',
                        bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                        fontSize: '14px',
                      },
                    }}
                  />
                </Box>

                {/* Description */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                    Task Description <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
                  </Typography>
                  <TextField
                    name="description"
                    placeholder="Enter details, action required, or patient notes..."
                    value={values.description}
                    onChange={handleChange}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    variant="outlined"
                    InputProps={{
                      sx: {
                        borderRadius: '10px',
                        bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                        fontSize: '14px',
                      },
                    }}
                  />
                </Box>

                {/* Assign To */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                    Assigned Staff Members <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
                  </Typography>
                  <FormControl fullWidth size="small" error={touched.assigned && Boolean(errors.assigned)}>
                    <Select
                      multiple
                      displayEmpty
                      value={values.assigned}
                      onChange={(e) => setFieldValue('assigned', e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) {
                          return (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '14px' }}>
                              Select staff members...
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                            {selected.map((val) => {
                              const staff = allStaffOptions.find((o) => o.value === val);
                              return (
                                <Chip
                                  key={val}
                                  label={staff?.name || val}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                      sx={{
                        borderRadius: '10px',
                        bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                        fontSize: '14px',
                      }}
                    >
                      {allStaffOptions.length === 0 ? (
                        <MenuItem disabled>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            No staff members found
                          </Typography>
                        </MenuItem>
                      ) : (
                        allStaffOptions.map((staff) => (
                          <MenuItem key={staff.value} value={staff.value} sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <PersonIcon sx={{ fontSize: 16 }} />
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                                  {staff.name || staff.label}
                                </Typography>
                                {staff.value && staff.value !== staff.name && (
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px', display: 'block' }}>
                                    {staff.value}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.assigned && errors.assigned && (
                      <Typography variant="caption" sx={{ color: '#DC2626', mt: 0.5, ml: 1.5, fontSize: '12px' }}>
                        {errors.assigned}
                      </Typography>
                    )}
                  </FormControl>
                </Box>

                {/* Priority & Due Date Row */}
                <Grid container spacing={2}>
                  {/* Priority Select */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                      Priority Level <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
                    </Typography>
                    <TextField
                      select
                      name="priority"
                      value={values.priority}
                      onChange={handleChange}
                      error={touched.priority && Boolean(errors.priority)}
                      helperText={touched.priority && errors.priority}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        sx: {
                          borderRadius: '10px',
                          bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                          fontSize: '14px',
                        },
                      }}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Chip
                            label={option.label}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '11px',
                              fontWeight: 700,
                              borderRadius: '6px',
                              bgcolor: isDark ? alpha(option.color, 0.2) : option.bg,
                              color: option.color,
                            }}
                          />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Due Date */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                      Due Date <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
                    </Typography>
                    <TextField
                      name="dueDate"
                      type="date"
                      value={values.dueDate}
                      onChange={handleChange}
                      error={touched.dueDate && Boolean(errors.dueDate)}
                      helperText={touched.dueDate && errors.dueDate}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        sx: {
                          borderRadius: '10px',
                          bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                          fontSize: '14px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Box>

            <Divider sx={{ borderColor: theme.palette.divider }} />

            {/* Actions */}
            <Box
              sx={{
                p: 2,
                px: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: isDark ? alpha(theme.palette.background.default, 0.6) : '#F8FAFC',
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '14px',
                  px: 2.5,
                  py: 0.9,
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.primary,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '14px',
                  px: 3,
                  py: 0.9,
                  textTransform: 'none',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText || '#FFFFFF',
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                {submitting ? 'Creating...' : 'Create Task'}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default AddTask;
